import classNames from 'classnames'
import { useEffect, useState } from 'react'

import useLoggedIn from '@src/auth/hooks/auth.hook'
import { Icon, Link } from '@src/quince-components/src'
import ShareIcon from '@src/quince-components/src/icons/link.inline.svg'
import useFetch from '@src/quince-site-common/src/hooks/useFetch'
import wishlistService from '@src/quince-site-common/src/services/wishlistService'
import isBrowser from '@src/quince-site-common/src/utils/isBrowser'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { trackEvent } from '@src/telemetry/track'

import styles from './wishlist-share-section.module.scss'

export interface WishlistShareSectionProps {
  wishListProductList: {
    product_id: number
    color?: string
  }[]
}

function WishlistShareSection({ wishListProductList }: WishlistShareSectionProps) {
  const [showMsg, setShowMsg] = useState<boolean>(false)
  const [hasError, setHasError] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(false)

  const {
    fetchData: getUserToken,
    isLoading: isTokenLoading,
    response: wishlistTokenResponse,
  } = useFetch(wishlistService.getWishlistToken, {})

  const isLoggedIn = useLoggedIn()

  const showMessage = () => {
    setShowMsg(true)
    setTimeout(() => {
      setShowMsg(false)
    }, 3000)
  }

  const handleWishlistShare = async () => {
    if (isLoading) return
    try {
      setIsLoading(true)
      setHasError(false)
      if (!wishlistTokenResponse?.wishlist_token) {
        throw new Error('Tokken not generated from API!!!')
      }
      trackEvent(TELEMETRY_EVENT_NAMES.SHARE_WISHLIST, {
        wishlistToken: wishlistTokenResponse?.wishlist_token,
        wishlistProductList: wishListProductList,
      })
      const url = new URL(window.location.href)
      url.searchParams.append('id', wishlistTokenResponse.wishlist_token)
      url.searchParams.append('utm_medium', 'organic_social')
      url.searchParams.append('utm_source', 'wishlist_share')
      await navigator.clipboard.writeText(url.href)
    } catch (err) {
      setHasError(true)
      console.error('Failed to copy: ', err)
    } finally {
      showMessage()
      setIsLoading(false)
    }
  }

  function onShareButtonClick() {
    handleWishlistShare().catch((err: unknown) => {
      console.error('Error while sharing wishlist, onShareButtonClick():: ', err)
    })
  }

  useEffect(() => {
    if (isLoggedIn) {
      getUserToken().catch((err: unknown) => {
        console.error('Error while fetching wishlist token, getUserToken():: ', err)
      })
    }
  }, [getUserToken, isLoggedIn])

  return (
    <div className={styles.wishlistShareWrapper}>
      {isLoggedIn ? (
        <button
          className={classNames(styles.wishlistShareBtn, {
            [styles.loading]: isLoading,
          })}
          disabled={isLoading || isTokenLoading}
          onClick={onShareButtonClick}
        >
          SHARE
          <Icon iconSvg={ShareIcon} className={styles.shareIcon} />
        </button>
      ) : (
        <Link
          className={styles.wishlistShareBtn}
          href={`/log-in?redirecturi=${isBrowser() ? window.location.href : ''}`}
        >
          SIGN IN TO SHARE
          <Icon iconSvg={ShareIcon} className={styles.shareIcon} />
        </Link>
      )}
      <div
        className={classNames(styles.shareSuccessMsgWrapper, {
          [styles.error]: hasError,
        })}
      >
        <div
          className={classNames(styles.wishlistShareSuccessMsg, {
            [styles.show]: showMsg,
          })}
        >
          {hasError ? 'Something went wrong' : 'Link copied!'}
        </div>
      </div>
    </div>
  )
}

export default WishlistShareSection
